// Pure TypeScript workspace logic — ZERO React imports
// Handles URL serialization, filter manipulation, and workspace state transitions

import type {
  TaskPriority,
  TaskStatus,
  TaskType,
  TaskView,
  TaskViewDisplay,
  TaskViewFilter,
  TaskViewType,
} from '@core/types/task';

import type {
  DensityMode,
  FilterValue,
  GroupOption,
  SortDirection,
  ViewType,
  WorkspaceConfig,
  WorkspaceState,
} from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DENSITY_HEIGHTS: Record<DensityMode, number> = {
  compact: 32,
  normal: 44,
  relaxed: 64,
};

export const DEFAULT_WORKSPACE_STATE: WorkspaceState = {
  view: 'list',
  filters: [],
  groupBy: null,
  sortBy: null,
  sortDirection: 'asc',
  density: 'normal',
  search: '',
  viewId: null,
};

// ---------------------------------------------------------------------------
// Task workspace configuration
// ---------------------------------------------------------------------------

const TASK_VIEWS: WorkspaceConfig['availableViews'] = [
  { id: 'list', labelKey: 'workspace.view.list', icon: 'list', available: true },
  { id: 'board', labelKey: 'workspace.view.board', icon: 'board', available: true },
  { id: 'timeline', labelKey: 'workspace.view.timeline', icon: 'timeline', available: true },
  { id: 'calendar', labelKey: 'workspace.view.calendar', icon: 'calendar', available: true },
  { id: 'schedule', labelKey: 'workspace.view.schedule', icon: 'schedule', available: true },
];

// Filter option values are the wire enum names (TASK_STATUS_OPEN, ...) rather
// than short slugs. Keeping the wire value in the filter chip removes a
// translation layer between the URL, the UI and TaskViewFilter, so a filter can
// never silently stop matching because a slug drifted from its enum.
const TASK_FILTERS: WorkspaceConfig['availableFilters'] = [
  {
    field: 'status',
    labelKey: 'workspace.filter.status._',
    type: 'multi-select',
    options: [
      { value: 'TASK_STATUS_DRAFT', labelKey: 'workspace.filter.status.draft' },
      { value: 'TASK_STATUS_OPEN', labelKey: 'workspace.filter.status.open' },
      { value: 'TASK_STATUS_IN_PROGRESS', labelKey: 'workspace.filter.status.in_progress' },
      { value: 'TASK_STATUS_IN_REVIEW', labelKey: 'workspace.filter.status.in_review' },
      { value: 'TASK_STATUS_BLOCKED', labelKey: 'workspace.filter.status.blocked' },
      { value: 'TASK_STATUS_DONE', labelKey: 'workspace.filter.status.done' },
      { value: 'TASK_STATUS_CANCELLED', labelKey: 'workspace.filter.status.cancelled' },
      { value: 'TASK_STATUS_ARCHIVED', labelKey: 'workspace.filter.status.archived' },
    ],
  },
  {
    field: 'priority',
    labelKey: 'workspace.filter.priority._',
    type: 'multi-select',
    options: [
      { value: 'TASK_PRIORITY_HIGHEST', labelKey: 'workspace.filter.priority.urgent' },
      { value: 'TASK_PRIORITY_HIGH', labelKey: 'workspace.filter.priority.high' },
      { value: 'TASK_PRIORITY_MEDIUM', labelKey: 'workspace.filter.priority.medium' },
      { value: 'TASK_PRIORITY_LOW', labelKey: 'workspace.filter.priority.low' },
      { value: 'TASK_PRIORITY_LOWEST', labelKey: 'workspace.filter.priority.lowest' },
    ],
  },
  { field: 'assignee', labelKey: 'workspace.filter.assignee', type: 'user' },
  { field: 'creator', labelKey: 'workspace.filter.creator', type: 'user' },
  {
    field: 'type',
    labelKey: 'workspace.filter.type._',
    type: 'multi-select',
    options: [
      { value: 'TASK_TYPE_TASK', labelKey: 'workspace.filter.type.task' },
      { value: 'TASK_TYPE_BUG', labelKey: 'workspace.filter.type.bug' },
      { value: 'TASK_TYPE_FEATURE', labelKey: 'workspace.filter.type.feature' },
      { value: 'TASK_TYPE_IMPROVEMENT', labelKey: 'workspace.filter.type.improvement' },
      { value: 'TASK_TYPE_SUPPORT', labelKey: 'workspace.filter.type.support' },
      { value: 'TASK_TYPE_QUESTION', labelKey: 'workspace.filter.type.question' },
      { value: 'TASK_TYPE_SUBTASK', labelKey: 'workspace.filter.type.subtask' },
      { value: 'TASK_TYPE_MILESTONE', labelKey: 'workspace.filter.type.milestone' },
    ],
  },
  { field: 'label', labelKey: 'workspace.filter.label', type: 'multi-select' },
  { field: 'due_date', labelKey: 'workspace.filter.due_date', type: 'date-range' },
];

const ALL_GROUPABLE_VIEWS: ViewType[] = ['list', 'board', 'timeline', 'calendar', 'schedule'];

const TASK_GROUPS: WorkspaceConfig['availableGroups'] = [
  { field: 'status', labelKey: 'workspace.group.status', availableInViews: ALL_GROUPABLE_VIEWS },
  { field: 'priority', labelKey: 'workspace.group.priority', availableInViews: ALL_GROUPABLE_VIEWS },
  { field: 'assignee', labelKey: 'workspace.group.assignee', availableInViews: ALL_GROUPABLE_VIEWS },
  { field: 'type', labelKey: 'workspace.group.type', availableInViews: ALL_GROUPABLE_VIEWS },
  { field: 'label', labelKey: 'workspace.group.label', availableInViews: ['list', 'board'] },
  { field: 'parent', labelKey: 'workspace.group.parent', availableInViews: ['list', 'board'] },
  { field: 'sprint', labelKey: 'workspace.group.sprint', availableInViews: ALL_GROUPABLE_VIEWS },
  { field: 'project', labelKey: 'workspace.group.project', availableInViews: ALL_GROUPABLE_VIEWS },
  { field: 'none', labelKey: 'workspace.group.none', availableInViews: ALL_GROUPABLE_VIEWS },
];

const TASK_SORTS: WorkspaceConfig['availableSorts'] = [
  { field: 'created_at', labelKey: 'workspace.sort.created_at' },
  { field: 'updated_at', labelKey: 'workspace.sort.updated_at' },
  { field: 'due_date', labelKey: 'workspace.sort.due_date' },
  { field: 'priority', labelKey: 'workspace.sort.priority' },
  { field: 'title', labelKey: 'workspace.sort.title' },
];

export const TASK_WORKSPACE_CONFIG: WorkspaceConfig = {
  availableViews: TASK_VIEWS,
  availableFilters: TASK_FILTERS,
  availableGroups: TASK_GROUPS,
  availableSorts: TASK_SORTS,
  defaultView: 'list',
  defaultGroupBy: 'status',
  defaultSortBy: 'created_at',
  defaultSortDirection: 'desc',
  defaultDensity: 'normal',
};

// ---------------------------------------------------------------------------
// Document workspace configuration
// ---------------------------------------------------------------------------

const DOCUMENT_FILTERS: WorkspaceConfig['availableFilters'] = [
  {
    field: 'status',
    labelKey: 'workspace.filter.status._',
    type: 'select',
    options: [
      { value: 'draft', labelKey: 'document.status.draft' },
      { value: 'in_review', labelKey: 'document.status.inReview' },
      { value: 'published', labelKey: 'document.status.published' },
      { value: 'archived', labelKey: 'document.status.archived' },
    ],
  },
];

export const DOCUMENT_WORKSPACE_CONFIG: WorkspaceConfig = {
  // The document page owns its four wiki views (including graph) and persists
  // that choice per profile. A second switcher in the workspace navbar used
  // separate state and could disagree with the visible document layout.
  availableViews: [],
  availableFilters: DOCUMENT_FILTERS,
  availableGroups: [],
  availableSorts: [],
  defaultView: 'grid',
  defaultDensity: 'normal',
};

// The chronicle is one flow, not a view switcher: no tree/grid/table tabs,
// no density — the feed owns its own layout.
export const DOCUMENT_CHRONICLE_CONFIG: WorkspaceConfig = {
  ...DOCUMENT_WORKSPACE_CONFIG,
  availableViews: [],
};

// ---------------------------------------------------------------------------
// URL parsing / serialization
// ---------------------------------------------------------------------------

const VALID_VIEWS = new Set<string>([
  'list', 'board', 'timeline', 'calendar', 'schedule',
  'tree', 'grid', 'table', 'registry', 'review', 'expiring',
]);

const VALID_DENSITIES = new Set<string>(['compact', 'normal', 'relaxed']);

const VALID_SORT_DIRECTIONS = new Set<string>(['asc', 'desc']);

const VALID_OPERATORS = new Set<string>([
  'eq', 'in', 'gt', 'lt', 'gte', 'lte', 'between', 'contains',
]);

/**
 * Parse a single URL-encoded filter string into a FilterValue.
 * Format: `field:operator:value` where value may contain colons.
 * Multi-values are comma-separated; range values use `~` separator.
 */
function parseFilterParam(raw: string): FilterValue | null {
  const firstColon = raw.indexOf(':');
  if (firstColon === -1) return null;

  const secondColon = raw.indexOf(':', firstColon + 1);
  if (secondColon === -1) return null;

  const field = raw.slice(0, firstColon);
  const operator = raw.slice(firstColon + 1, secondColon);
  const rawValue = raw.slice(secondColon + 1);

  if (!field || !VALID_OPERATORS.has(operator)) return null;

  let value: FilterValue['value'];
  if (operator === 'between') {
    const parts = rawValue.split('~');
    if (parts.length !== 2) return null;
    value = [parts[0], parts[1]] as [string, string];
  } else if (operator === 'in') {
    value = rawValue.split(',').filter(Boolean);
  } else {
    value = rawValue;
  }

  return { field, operator: operator as FilterValue['operator'], value };
}

function serializeFilterParam(filter: FilterValue): string {
  let valueStr: string;
  if (filter.operator === 'between' && Array.isArray(filter.value)) {
    valueStr = (filter.value as [string, string]).join('~');
  } else if (filter.operator === 'in' && Array.isArray(filter.value)) {
    valueStr = (filter.value as string[]).join(',');
  } else {
    valueStr = String(filter.value);
  }
  return `${filter.field}:${filter.operator}:${valueStr}`;
}

/**
 * Build a WorkspaceState from URL search params, falling back to config
 * defaults for any missing values.
 */
export function parseWorkspaceStateFromUrl(
  searchParams: URLSearchParams,
  config: WorkspaceConfig,
): WorkspaceState {
  const defaults = buildDefaults(config);

  const rawView = searchParams.get('view');
  const view: ViewType = rawView && VALID_VIEWS.has(rawView)
    ? (rawView as ViewType)
    : defaults.view;

  const filters: FilterValue[] = [];
  const filterParams = searchParams.getAll('filter');
  for (const raw of filterParams) {
    const parsed = parseFilterParam(raw);
    if (parsed) filters.push(parsed);
  }

  const rawGroup = searchParams.get('group');
  const groupBy = rawGroup !== null ? (rawGroup || null) : defaults.groupBy;

  const rawSort = searchParams.get('sort');
  const sortBy = rawSort !== null ? (rawSort || null) : defaults.sortBy;

  const rawDir = searchParams.get('dir');
  const sortDirection: SortDirection = rawDir && VALID_SORT_DIRECTIONS.has(rawDir)
    ? (rawDir as SortDirection)
    : defaults.sortDirection;

  const rawDensity = searchParams.get('density');
  const density: DensityMode = rawDensity && VALID_DENSITIES.has(rawDensity)
    ? (rawDensity as DensityMode)
    : defaults.density;

  const search = searchParams.get('q') ?? defaults.search;

  const viewId = searchParams.get('saved') || null;

  return { view, filters, groupBy, sortBy, sortDirection, density, search, viewId };
}

/**
 * Serialize workspace state to URL params. Only includes values that differ
 * from defaults so URLs stay clean.
 */
export function serializeWorkspaceStateToUrl(
  state: WorkspaceState,
  defaults: WorkspaceState,
): URLSearchParams {
  const params = new URLSearchParams();

  if (state.view !== defaults.view) {
    params.set('view', state.view);
  }

  for (const filter of state.filters) {
    params.append('filter', serializeFilterParam(filter));
  }

  if (state.groupBy !== defaults.groupBy) {
    params.set('group', state.groupBy ?? '');
  }

  if (state.sortBy !== defaults.sortBy) {
    params.set('sort', state.sortBy ?? '');
  }

  if (state.sortDirection !== defaults.sortDirection) {
    params.set('dir', state.sortDirection);
  }

  if (state.density !== defaults.density) {
    params.set('density', state.density);
  }

  if (state.search !== defaults.search && state.search !== '') {
    params.set('q', state.search);
  }

  // The saved view rides along so a shared link opens on the same view. The
  // filters are serialized as well rather than looked up from the view: the
  // link then still works for someone who cannot see that view, and it keeps
  // working after the view's owner edits it.
  if (state.viewId) {
    params.set('saved', state.viewId);
  }

  return params;
}

// ---------------------------------------------------------------------------
// Immutable state transitions
// ---------------------------------------------------------------------------

export function addFilter(state: WorkspaceState, filter: FilterValue): WorkspaceState {
  // Replace existing filter on the same field, or append
  const existing = state.filters.findIndex((f) => f.field === filter.field);
  const filters = existing >= 0
    ? state.filters.map((f, i) => (i === existing ? filter : f))
    : [...state.filters, filter];
  return { ...state, filters };
}

export function removeFilter(state: WorkspaceState, field: string): WorkspaceState {
  return { ...state, filters: state.filters.filter((f) => f.field !== field) };
}

export function clearFilters(state: WorkspaceState): WorkspaceState {
  return { ...state, filters: [] };
}

export function setView(state: WorkspaceState, view: ViewType): WorkspaceState {
  return { ...state, view };
}

export function setGroupBy(state: WorkspaceState, groupBy: string | null): WorkspaceState {
  return { ...state, groupBy: groupBy === 'none' ? null : groupBy };
}

export function setSortBy(
  state: WorkspaceState,
  field: string,
  direction?: SortDirection,
): WorkspaceState {
  // If sorting by the same field without explicit direction, toggle direction
  const sortDirection: SortDirection =
    direction ??
    (state.sortBy === field && state.sortDirection === 'asc' ? 'desc' : 'asc');

  return { ...state, sortBy: field, sortDirection };
}

export function setDensity(state: WorkspaceState, density: DensityMode): WorkspaceState {
  return { ...state, density };
}

export function setSearch(state: WorkspaceState, search: string): WorkspaceState {
  return { ...state, search };
}

// ---------------------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------------------

/**
 * Return only the group options available for the given view type.
 */
export function getAvailableGroups(config: WorkspaceConfig, view: ViewType): GroupOption[] {
  return config.availableGroups.filter((g) => g.availableInViews.includes(view));
}

/**
 * Convert workspace filters into a flat record suitable for API query params.
 *
 * Conventions:
 * - `eq`       → `{ field: value }`
 * - `in`       → `{ field: "a,b,c" }`
 * - `contains` → `{ field: value }`
 * - `gt/lt/gte/lte` → `{ field_gt: value }` etc.
 * - `between`  → `{ field_gte: from, field_lte: to }`
 */
export function buildApiFilters(state: WorkspaceState): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const filter of state.filters) {
    switch (filter.operator) {
      case 'eq':
      case 'contains':
        result[filter.field] = filter.value;
        break;
      case 'in':
        result[filter.field] = Array.isArray(filter.value)
          ? (filter.value as string[]).join(',')
          : filter.value;
        break;
      case 'gt':
      case 'lt':
      case 'gte':
      case 'lte':
        result[`${filter.field}_${filter.operator}`] = filter.value;
        break;
      case 'between': {
        const [from, to] = filter.value as [string, string];
        result[`${filter.field}_gte`] = from;
        result[`${filter.field}_lte`] = to;
        break;
      }
    }
  }

  if (state.search) {
    result['q'] = state.search;
  }

  if (state.sortBy) {
    result['sort'] = state.sortBy;
    result['dir'] = state.sortDirection;
  }

  if (state.groupBy) {
    result['group'] = state.groupBy;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Task request mapping (structured TaskViewFilter)
// ---------------------------------------------------------------------------

/** True when the user narrowed the list in any way (chips or free-text search). */
export function hasActiveFilters(state: WorkspaceState): boolean {
  return state.filters.length > 0 || state.search.trim() !== '';
}

/** Normalize a FilterValue into a flat list of string values. */
function filterValues(filter: FilterValue): string[] {
  if (Array.isArray(filter.value)) {
    return (filter.value as string[]).filter(Boolean);
  }
  const single = String(filter.value ?? '').trim();
  return single ? [single] : [];
}

/** `YYYY-MM-DD` (or any parseable date) → ISO timestamp at the start of that day. */
function toDayStartIso(raw: string): string | undefined {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
}

/** `YYYY-MM-DD` (or any parseable date) → ISO timestamp at the end of that day. */
function toDayEndIso(raw: string): string | undefined {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
}

/**
 * Map workspace state onto the structured `TaskViewFilter` the task service
 * expects (see ListTasksRequest / ListMyTasksRequest / ListTasksByParentRequest).
 *
 * Returns `undefined` when nothing is narrowed, so an unfiltered request stays
 * free of empty filter objects.
 */
export function buildTaskViewFilter(state: WorkspaceState): TaskViewFilter | undefined {
  const filter: TaskViewFilter = {
    tags: [],
    assignee_ids: [],
    creator_ids: [],
    aspect_types: [],
    watcher_task_ids: [],
    parent_ids: [],
    match_any: false,
  };
  let narrowed = false;

  for (const entry of state.filters) {
    const values = filterValues(entry);

    switch (entry.field) {
      case 'status':
        if (values.length) {
          filter.statuses = { statuses: values as TaskStatus[] };
          narrowed = true;
        }
        break;

      case 'priority':
        if (values.length) {
          filter.priorities = { priorities: values as TaskPriority[] };
          narrowed = true;
        }
        break;

      case 'type':
        if (values.length) {
          filter.types = { types: values as TaskType[] };
          narrowed = true;
        }
        break;

      case 'assignee':
        if (values.length) {
          filter.assignee_ids = values;
          narrowed = true;
        }
        break;

      case 'creator':
        if (values.length) {
          filter.creator_ids = values;
          narrowed = true;
        }
        break;

      case 'label':
        if (values.length) {
          filter.tags = values;
          narrowed = true;
        }
        break;

      case 'due_date': {
        if (entry.operator === 'between' && Array.isArray(entry.value)) {
          const [from, to] = entry.value as [string, string];
          const after = from ? toDayStartIso(from) : undefined;
          const before = to ? toDayEndIso(to) : undefined;
          if (after) filter.due_after = after;
          if (before) filter.due_before = before;
          if (after || before) narrowed = true;
        } else if (values.length) {
          const isUpperBound = entry.operator === 'lt' || entry.operator === 'lte';
          const bound = isUpperBound ? toDayEndIso(values[0]) : toDayStartIso(values[0]);
          if (bound) {
            if (isUpperBound) {
              filter.due_before = bound;
            } else {
              filter.due_after = bound;
            }
            narrowed = true;
          }
        }
        break;
      }

      default:
        break;
    }
  }

  const query = state.search.trim();
  if (query) {
    filter.search_query = query;
    narrowed = true;
  }

  return narrowed ? filter : undefined;
}

/**
 * Map workspace sort state onto the list query.
 *
 * The gateway's conventional `sort=field:order` parameter is used rather than
 * the per-request `sort_by`/`sort_desc` pair, because only ListTasks carries
 * those fields — ListMyTasks and ListTasksByParent do not, and every list
 * endpoint understands `sort`.
 *
 * Returns an empty object when no sort is active so the caller can spread it.
 */
export function buildTaskSort(state: WorkspaceState): { sort?: string } {
  if (!state.sortBy) return {};
  return { sort: `${state.sortBy}:${state.sortDirection}` };
}

// ---------------------------------------------------------------------------
// Saved views: WorkspaceState ⇄ TaskView
//
// A saved view stores what to look at (TaskViewFilter) and how to look at it
// (TaskViewDisplay); the workspace holds the same information in the flat,
// URL-friendly shape the toolbar edits. Everything below is a pure conversion
// between the two, so both directions can be tested without a store, a router
// or a rendered toolbar.
//
// Two things deliberately do not round-trip:
//
//  - `density` is a per-person reading preference, not part of what a view
//    describes, and the proto has no field for it. Applying a view leaves the
//    current density alone.
//  - the workspace's `schedule` view has no TaskViewType. It is persisted as
//    CALENDAR (see workspaceViewToTaskViewType) — the closest planning view —
//    so that saving from the schedule tab still saves the filters, which is
//    what the user came for. Reopening the view lands on calendar. The
//    alternative, refusing to save while on schedule, costs the user the whole
//    feature to protect one field. The save dialog says so rather than
//    silently downgrading. A lossless fix belongs in the proto
//    (TASK_VIEW_TYPE_SCHEDULE), not in a metadata side-channel that only this
//    client would understand.
// ---------------------------------------------------------------------------

const WORKSPACE_VIEW_TO_TASK_VIEW_TYPE: Partial<Record<ViewType, TaskViewType>> = {
  list: 'TASK_VIEW_TYPE_LIST',
  board: 'TASK_VIEW_TYPE_BOARD',
  calendar: 'TASK_VIEW_TYPE_CALENDAR',
  timeline: 'TASK_VIEW_TYPE_TIMELINE',
  schedule: 'TASK_VIEW_TYPE_CALENDAR',
};

const TASK_VIEW_TYPE_TO_WORKSPACE_VIEW: Partial<Record<TaskViewType, ViewType>> = {
  TASK_VIEW_TYPE_LIST: 'list',
  TASK_VIEW_TYPE_BOARD: 'board',
  TASK_VIEW_TYPE_CALENDAR: 'calendar',
  TASK_VIEW_TYPE_TIMELINE: 'timeline',
};

/** The proto view type a workspace view is stored as. */
export function workspaceViewToTaskViewType(view: ViewType): TaskViewType {
  return WORKSPACE_VIEW_TO_TASK_VIEW_TYPE[view] ?? 'TASK_VIEW_TYPE_LIST';
}

/** The workspace view a stored view type reopens on, or `null` if unknown. */
export function taskViewTypeToWorkspaceView(viewType?: TaskViewType): ViewType | null {
  if (!viewType) return null;
  return TASK_VIEW_TYPE_TO_WORKSPACE_VIEW[viewType] ?? null;
}

/** True when saving this view type loses information (schedule → calendar). */
export function isViewTypeApproximated(view: ViewType): boolean {
  return taskViewTypeToWorkspaceView(workspaceViewToTaskViewType(view)) !== view;
}

/** A TaskViewFilter with every repeated field present and nothing narrowed. */
export function emptyTaskViewFilter(): TaskViewFilter {
  return {
    tags: [],
    assignee_ids: [],
    creator_ids: [],
    aspect_types: [],
    watcher_task_ids: [],
    parent_ids: [],
    match_any: false,
  };
}

/**
 * The filter to persist for the current workspace state.
 *
 * Unlike `buildTaskViewFilter` this always returns an object: "no filters" is a
 * legitimate saved view ("everything, grouped by assignee") and must overwrite
 * a previously saved narrowing rather than leave it in place.
 *
 * `base` carries over the fields the workspace toolbar cannot edit today
 * (creator_ids, aspect_types, watchers, parents, match_any). Without it,
 * re-saving a view built elsewhere would quietly erase them.
 */
export function buildSavedViewFilter(
  state: WorkspaceState,
  base?: TaskViewFilter,
): TaskViewFilter {
  const carried = base ?? emptyTaskViewFilter();
  return {
    ...(buildTaskViewFilter(state) ?? emptyTaskViewFilter()),
    creator_ids: carried.creator_ids ?? [],
    aspect_types: carried.aspect_types ?? [],
    watcher_task_ids: carried.watcher_task_ids ?? [],
    parent_ids: carried.parent_ids ?? [],
    parent: carried.parent,
    match_any: carried.match_any ?? false,
  };
}

/** The display block to persist for the current workspace state. */
export function buildSavedViewDisplay(
  state: WorkspaceState,
  base?: TaskViewDisplay,
): TaskViewDisplay {
  return {
    view_type: workspaceViewToTaskViewType(state.view),
    sort_by: state.sortBy ?? '',
    sort_desc: state.sortDirection === 'desc',
    // Column visibility has no toolbar control yet; keep whatever the view
    // already carried instead of blanking it on every save.
    visible_columns: base?.visible_columns ?? [],
    // `none` is the workspace's sentinel for "no grouping" (see setGroupBy).
    // Writing it, rather than an empty string, keeps "ungrouped" distinct from
    // "unset" so applying the view does not silently restore the default group.
    group_by: state.groupBy ?? 'none',
  };
}

/** `2026-07-27T00:00:00.000Z` → `2026-07-27` in the reader's own timezone. */
function isoToDateInput(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Rebuild the toolbar's filter chips from a stored TaskViewFilter. */
export function taskViewFilterToWorkspaceFilters(filter?: TaskViewFilter): FilterValue[] {
  if (!filter) return [];
  const filters: FilterValue[] = [];

  const push = (field: string, values?: string[]) => {
    const clean = (values ?? []).filter(Boolean);
    if (clean.length) filters.push({ field, operator: 'in', value: clean });
  };

  push('status', filter.statuses?.statuses as string[] | undefined);
  push('priority', filter.priorities?.priorities as string[] | undefined);
  push('type', filter.types?.types as string[] | undefined);
  push('assignee', filter.assignee_ids);
  push('label', filter.tags);

  // A one-sided range keeps the other end empty rather than switching operator:
  // the chip's editor only knows how to show `between`.
  const after = isoToDateInput(filter.due_after);
  const before = isoToDateInput(filter.due_before);
  if (after || before) {
    filters.push({ field: 'due_date', operator: 'between', value: [after, before] });
  }

  return filters;
}

/**
 * Apply a saved view onto the workspace: filters, search, view type, sort and
 * grouping. `defaults` fills in whatever the view left unset.
 */
export function applySavedViewToState(
  state: WorkspaceState,
  view: TaskView,
  defaults: WorkspaceState,
): WorkspaceState {
  const display = view.display;
  const mappedView = taskViewTypeToWorkspaceView(display?.view_type);
  const groupBy = display?.group_by;

  return {
    ...state,
    viewId: view.entity?.id ?? null,
    view: mappedView ?? defaults.view,
    filters: taskViewFilterToWorkspaceFilters(view.filters),
    search: view.filters?.search_query?.trim() ?? '',
    sortBy: display?.sort_by ? display.sort_by : defaults.sortBy,
    sortDirection: display?.sort_by ? (display.sort_desc ? 'desc' : 'asc') : defaults.sortDirection,
    groupBy: groupBy === undefined || groupBy === ''
      ? defaults.groupBy
      : (groupBy === 'none' ? null : groupBy),
  };
}

/** Drop the active saved view, keeping everything the user is looking at. */
export function detachSavedView(state: WorkspaceState): WorkspaceState {
  return { ...state, viewId: null };
}

function sortedValues(values?: string[]): string[] {
  return [...(values ?? [])].filter(Boolean).sort();
}

/** Order-insensitive identity of a filter, for change detection. */
function filterSignature(filter?: TaskViewFilter): string {
  const value = filter ?? emptyTaskViewFilter();
  return JSON.stringify({
    statuses: sortedValues(value.statuses?.statuses as string[] | undefined),
    priorities: sortedValues(value.priorities?.priorities as string[] | undefined),
    types: sortedValues(value.types?.types as string[] | undefined),
    tags: sortedValues(value.tags),
    assignee_ids: sortedValues(value.assignee_ids),
    creator_ids: sortedValues(value.creator_ids),
    aspect_types: sortedValues(value.aspect_types),
    watcher_task_ids: sortedValues(value.watcher_task_ids),
    parent_ids: sortedValues(value.parent_ids),
    parent: value.parent?.id ?? '',
    due_after: value.due_after ?? '',
    due_before: value.due_before ?? '',
    search_query: (value.search_query ?? '').trim(),
    match_any: !!value.match_any,
  });
}

/** Identity of a display block, for change detection. */
function displaySignature(display?: TaskViewDisplay): string {
  const viewType = display?.view_type;
  return JSON.stringify({
    view_type: viewType && viewType !== 'TASK_VIEW_TYPE_UNSPECIFIED' ? viewType : '',
    sort_by: display?.sort_by ?? '',
    sort_desc: !!display?.sort_desc,
    group_by: display?.group_by ?? '',
    visible_columns: display?.visible_columns ?? [],
  });
}

/**
 * What a save would change on `view`, in `UpdateViewRequest.field_mask` terms.
 *
 * The stored blocks are not compared as they arrived but as they come back out
 * of `applySavedViewToState` — the state the view actually produces. A view
 * saved by an older client can carry values this workspace cannot express (an
 * unset `group_by`, an unknown view type); comparing raw would mark such a view
 * permanently modified and offer to "save" a change the user never made.
 */
export function savedViewFieldMask(
  state: WorkspaceState,
  view: TaskView,
  defaults: WorkspaceState,
  overrides?: { name?: string; is_shared?: boolean; is_default?: boolean },
): string[] {
  const mask: string[] = [];
  const applied = applySavedViewToState(state, view, defaults);

  if (overrides?.name !== undefined && overrides.name.trim() !== view.name) {
    mask.push('name');
  }
  if (
    filterSignature(buildSavedViewFilter(state, view.filters)) !==
    filterSignature(buildSavedViewFilter(applied, view.filters))
  ) {
    mask.push('filters');
  }
  if (
    displaySignature(buildSavedViewDisplay(state, view.display)) !==
    displaySignature(buildSavedViewDisplay(applied, view.display))
  ) {
    mask.push('display');
  }
  if (overrides?.is_shared !== undefined && overrides.is_shared !== !!view.is_shared) {
    mask.push('is_shared');
  }
  if (overrides?.is_default !== undefined && overrides.is_default !== !!view.is_default) {
    mask.push('is_default');
  }

  return mask;
}

/**
 * True when the workspace no longer shows what the view describes — the "you
 * have unsaved changes" dot.
 */
export function isSavedViewModified(
  state: WorkspaceState,
  view: TaskView,
  defaults: WorkspaceState,
): boolean {
  return savedViewFieldMask(state, view, defaults).length > 0;
}

/**
 * Saved views in the order the strip shows them: the default first, then
 * shared and personal views alphabetically, so the list is stable across
 * reloads regardless of how the server ordered them.
 */
export function sortSavedViews(views: TaskView[]): TaskView[] {
  return [...views].sort((a, b) => {
    if (!!a.is_default !== !!b.is_default) return a.is_default ? -1 : 1;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

/** The profile a view belongs to, or `''` for a view with no recorded owner. */
export function savedViewOwnerId(view: TaskView): string {
  const owner = view.owner as { entity?: { id?: string }; id?: string } | undefined;
  return owner?.entity?.id ?? owner?.id ?? '';
}

/**
 * Whether `profileId` may rename, overwrite or delete `view`.
 *
 * Mirrors the service (services/task/internal/services/v1/update_view.go): the
 * owner may edit, and a view created before owners were recorded stays editable
 * by anyone, since there is nobody to protect it for.
 */
export function canEditSavedView(view: TaskView, profileId: string | null): boolean {
  const owner = savedViewOwnerId(view);
  if (!owner) return true;
  return !!profileId && owner === profileId;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

export function buildDefaults(config: WorkspaceConfig): WorkspaceState {
  return {
    view: config.defaultView,
    filters: [],
    groupBy: config.defaultGroupBy ?? null,
    sortBy: config.defaultSortBy ?? null,
    sortDirection: config.defaultSortDirection ?? 'asc',
    density: config.defaultDensity ?? 'normal',
    search: '',
    viewId: null,
  };
}
